import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import MovieCard from '@/components/feature/MovieCard';
import type { Movie } from '@/types/content';
import { movies } from '@/mocks/movies';
import FilterSidebar, { type CategoryFilters } from './components/FilterSidebar';

const perPage = 8;

const sortOptions = [
  { key: 'new', label: '时间最新', icon: 'ri-time-line' },
  { key: 'hot', label: '热度最高', icon: 'ri-fire-line' },
  { key: 'rating', label: '评分推荐', icon: 'ri-star-line' },
];

export default function Category() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<CategoryFilters>({
    type: searchParams.get('type') ?? '全部',
    region: searchParams.get('region') ?? '全部',
    year: searchParams.get('year') ?? '全部',
    genre: searchParams.get('genre') ?? '全部',
  });
  const [sort, setSort] = useState('new');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filtered = useMemo(() => {
    const result = movies.filter((movie: Movie) => {
      if (filters.type !== '全部' && movie.type !== filters.type) return false;
      if (filters.region !== '全部' && movie.region !== filters.region) return false;
      if (filters.year !== '全部' && String(movie.year) !== filters.year) return false;
      if (filters.genre !== '全部' && !movie.genres.includes(filters.genre)) return false;
      return true;
    });
    return result.sort((a, b) => {
      if (sort === 'hot') return b.heat - a.heat;
      if (sort === 'rating') return b.rating - a.rating;
      return b.year - a.year;
    });
  }, [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleFilterChange = (key: keyof CategoryFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters({ type: '全部', region: '全部', year: '全部', genre: '全部' });
    setPage(1);
  };

  return (
    <PageLayout>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视分类' }]} />

        <div className="mt-6 rounded-lg bg-foreground-950 px-6 py-8 md:px-10 md:py-10">
          <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">影视分类</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-300 md:text-base">
            按类型、地区、年份与题材自由组合筛选，快速定位你想看的电影与电视剧。
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-6 lg:flex-row">
          <aside className="hidden w-full shrink-0 lg:block lg:w-64">
            <div className="sticky top-24">
              <FilterSidebar filters={filters} onChange={handleFilterChange} onReset={handleReset} />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 lg:hidden"
                >
                  <i className="ri-filter-3-line" />
                  筛选
                </button>
                <p className="text-sm text-foreground-500">
                  共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSort(option.key);
                      setPage(1);
                    }}
                    className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      sort === option.key
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={option.icon} />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {pageItems.length > 0 ? (
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-3xl" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有找到匹配的影视作品</h3>
                <p className="mt-2 text-sm text-foreground-500">尝试调整或重置筛选条件，发现更多精彩内容。</p>
                <button
                  type="button"
                  onClick={handleReset}
                  className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  重置筛选条件
                </button>
              </div>
            )}

            <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-base font-semibold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterSidebar filters={filters} onChange={handleFilterChange} onReset={handleReset} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-5 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看 {filtered.length} 个结果
            </button>
          </div>
        </div>
      )}
    </PageLayout>
  );
}